import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import HeroSectionLanding from '../Elements/HeroSectionLanding'
import LogosCarousel from '../Elements/LogosCarousel'
import { Link, useLocation } from 'react-router-dom'
import { track, trackScrollDepth } from '../../utils/analytics'
import { setStoredVariant } from '../../utils/experiment'
import {
  pilaresData,
  verticalesActivasData,
  kitsData,
  diferenciadorFeatureKeys,
  clientesLogosData,
  salidasData,
} from '../../Data/infoLandingPage'

const diferenciadorHardwareKeys = [
  { key: 'terminales', src: '/hardware/7.png', brands: 'Honeywell · Zebra' },
  { key: 'lectores', src: '/hardware/5.png', brands: 'Datalogic · Honeywell' },
  { key: 'impresoras', src: '/hardware/11.png', brands: 'Bixolon · Zebra' },
  { key: 'computo', src: '/hardware/10.png', brands: 'Zebra · Unitech' },
]

export default function LandingPage() {

  const { t } = useTranslation('landing')
  const location = useLocation()

  useEffect(() => {
    setStoredVariant('a')
    track('experiment_variant_view', { variant: 'a', page: 'home' })
    const cleanup = trackScrollDepth('a')
    return cleanup
  }, [])

  useEffect(() => {
    if (location.hash) {
      document.querySelector(location.hash)?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [location.hash])

  return (
    <div>
      <HeroSectionLanding />

      {/* EL DIFERENCIADOR */}
      <div className="flex min-h-screen items-center py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid grid-cols-1 items-center gap-16 lg:grid-cols-2">
            <div className="relative overflow-hidden rounded-2xl bg-gray-50 p-6">
              <div
                className="absolute -left-16 -top-16 h-56 w-56 rounded-full bg-electric-100 opacity-60 blur-3xl"
                aria-hidden="true"
              />
              <div className="relative grid grid-cols-2 gap-4">
                {diferenciadorHardwareKeys.map((item) => (
                  <div
                    key={item.key}
                    className="flex flex-col overflow-hidden rounded-xl bg-white ring-1 ring-gray-900/5"
                  >
                    <div className="flex h-28 items-center justify-center p-3 sm:h-32">
                      <img src={item.src} alt={t(`diferenciador.hardware.${item.key}.alt`)} className="h-full w-full object-contain" />
                    </div>
                    <div className="border-t border-gray-100 px-3 py-2.5 text-center">
                      <p className="text-sm font-bold text-navy-900">{t(`diferenciador.hardware.${item.key}.label`)}</p>
                      <p className="text-xs text-gray-400">{item.brands}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h2 className="text-base font-semibold leading-7 text-electric-600">{t('diferenciador.eyebrow')}</h2>
              <p className="mt-2 text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
                {t('diferenciador.heading')}
              </p>
              <p className="mt-8 text-xl leading-9 text-gray-600">
                {t('diferenciador.description')}
              </p>
              <div className="mt-10 grid grid-cols-1 gap-8 sm:grid-cols-2">
                {diferenciadorFeatureKeys.map((key) => (
                  <div key={key}>
                    <h3 className="text-base font-bold text-navy-900">{t(`diferenciador.features.${key}.title`)}</h3>
                    <p className="mt-2 text-base leading-7 text-gray-600">{t(`diferenciador.features.${key}.description`)}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CASOS DE ÉXITO */}
      <div id="casos-de-exito" className="relative isolate flex items-center overflow-hidden bg-navy-900 py-16 lg:min-h-screen">
        <div
          className="pointer-events-none absolute -left-24 -top-24 -z-10 h-72 w-72 rounded-full bg-gradient-to-tr from-electric-500/20 to-neon-300/10 blur-3xl"
          aria-hidden="true"
        />
        <div className="w-full">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="mx-auto max-w-2xl text-center">
              <h2 className="text-base font-semibold leading-7 text-neon-300">{t('casos.eyebrow')}</h2>
              <p className="mt-2 text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
                {t('casos.heading')}
              </p>
            </div>
          </div>
          <div className="mt-10 lg:mt-16">
            <LogosCarousel logos={clientesLogosData} />
          </div>
          <p className="mx-auto mt-6 max-w-2xl px-6 text-center text-xs text-white/40 lg:px-8">
            {t('casos.disclaimer')}
          </p>
        </div>
      </div>

      {/* INDUSTRIAS */}
      <div className="relative isolate flex min-h-screen items-center overflow-hidden py-24">
        <div
          className="pointer-events-none absolute -right-24 bottom-0 -z-10 h-72 w-72 rounded-full bg-electric-100 opacity-60 blur-3xl"
          aria-hidden="true"
        />
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-base font-semibold leading-7 text-electric-600">{t('industrias.eyebrow')}</h2>
            <p className="mt-2 text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
              {t('industrias.heading')}
            </p>
            <p className="mt-8 text-xl leading-9 text-gray-600">
              {t('industrias.description')}
            </p>
          </div>
          <div className="mx-auto mt-16 grid max-w-2xl grid-cols-1 gap-10 lg:max-w-none lg:grid-cols-3">
            {verticalesActivasData.map((vertical) => (
              <div
                key={vertical.key}
                className="group flex flex-col overflow-hidden rounded-2xl border border-gray-200"
              >
                <div className="overflow-hidden bg-gray-50 p-6">
                  <img
                    src={vertical.image}
                    alt={t(`industrias.verticales.${vertical.key}.name`)}
                    className="w-fit rounded-xl"
                  />
                </div>
                <div className="flex flex-1 flex-col p-8">
                  <h3 className="text-2xl font-bold text-navy-900">{t(`industrias.verticales.${vertical.key}.name`)}</h3>
                  <p className="mt-3 flex-auto text-base leading-7 text-gray-600">{t(`industrias.verticales.${vertical.key}.description`)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* PILARES */}
      <div id="pilares" className="relative isolate flex min-h-screen items-center overflow-hidden py-24">
        <div
          className="pointer-events-none absolute -left-24 top-0 -z-10 h-72 w-72 rounded-full bg-neon-100 opacity-60 blur-3xl"
          aria-hidden="true"
        />
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-base font-semibold leading-7 text-electric-600">{t('pilares.eyebrow')}</h2>
            <p className="mt-2 text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
              {t('pilares.heading')}
            </p>
            <p className="mt-8 text-xl leading-9 text-gray-600">
              {t('pilares.description')}
            </p>
          </div>
          <div className="mx-auto mt-16 grid max-w-2xl grid-cols-1 gap-10 lg:max-w-none lg:grid-cols-3">
            {pilaresData.map((c) => (
              <Link
                key={c.number}
                to={c.cta.to}
                state={c.cta.state}
                onClick={() => track('pillar_card_click', { variant: 'a', pillar: c.key })}
                className={`group flex flex-col overflow-hidden rounded-2xl transition-all duration-200 will-change-transform hover:-translate-y-1 focus-visible:-translate-y-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-electric-500 focus-visible:ring-offset-2 ${
                  c.highlighted
                    ? 'bg-electric-600 text-white shadow-md hover:bg-electric-500 hover:shadow-xl'
                    : 'border border-gray-200 hover:border-electric-300 hover:shadow-xl'
                }`}
              >
                <div className={`overflow-hidden p-6 ${c.highlighted ? 'bg-white/10' : 'bg-gray-50'}`}>
                  <img
                    src={c.image}
                    alt={t(`pilares.items.${c.key}.title`)}
                    className=" w-full h-48 rounded-xl object-fit transition-transform duration-300 group-hover:scale-105"
                  />
                </div>
                <div className="flex flex-1 flex-col p-8">
                  {/* <c.icon
                    className={`h-9 w-9 transition-transform duration-200 group-hover:scale-110 ${c.highlighted ? 'text-neon-300' : 'text-electric-600'}`}
                    aria-hidden="true"
                  /> */}
                  <span className={`mt-5 text-xs font-semibold uppercase tracking-wide ${c.highlighted ? 'text-white/70' : 'text-gray-400'}`}>
                    {c.number} &middot; {t(`pilares.items.${c.key}.fase`)}
                  </span>
                  <h3 className={`mt-2 text-2xl font-bold ${c.highlighted ? 'text-white' : 'text-navy-900'}`}>{t(`pilares.items.${c.key}.title`)}</h3>
                  <p className={`mt-4 flex-auto text-base leading-7 ${c.highlighted ? 'text-white/80' : 'text-gray-600'}`}>{t(`pilares.items.${c.key}.description`)}</p>
                  <span className={`mt-6 inline-flex items-center text-base font-semibold ${c.highlighted ? 'text-neon-300' : 'text-electric-600'}`}>
                    {t(`pilares.items.${c.key}.ctaLabel`)}
                    <span aria-hidden="true" className="ml-1 inline-block transition-transform duration-200 group-hover:translate-x-1">&rarr;</span>
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* KITS ESPECIALIZADOS */}
      <div id="kits-especializados" className="relative isolate flex min-h-screen items-center overflow-hidden py-24">
        <div
          className="pointer-events-none absolute inset-y-0 right-0 -z-10 hidden w-1/2 opacity-[0.4] lg:block"
          style={{
            backgroundImage: 'radial-gradient(circle, rgb(203, 213, 225) 1px, transparent 1px)',
            backgroundSize: '28px 28px',
            maskImage: 'radial-gradient(ellipse at right, black, transparent 70%)',
            WebkitMaskImage: 'radial-gradient(ellipse at right, black, transparent 70%)',
          }}
          aria-hidden="true"
        />
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-base font-semibold leading-7 text-electric-600">{t('kits.eyebrow')}</h2>
            <p className="mt-2 text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
              {t('kits.heading')}
            </p>
            <p className="mt-8 text-xl leading-9 text-gray-600">
              {t('kits.description')}
            </p>
          </div>
          <div className="mx-auto mt-16 grid max-w-2xl grid-cols-1 gap-10 lg:max-w-none lg:grid-cols-3">
            {kitsData.map((kit) => (
              <Link
                key={kit.key}
                to={kit.cta.to}
                state={kit.cta.state}
                onClick={() => track('product_card_click', { variant: 'a', kit: kit.key })}
                className="group flex flex-col overflow-hidden rounded-2xl border border-gray-200 "
              >
                <div className="overflow-hidden bg-gray-50 p-6">
                  <img
                    src={kit.image}
                    alt={t(`kits.items.${kit.key}.title`)}
                    className="w-fit rounded-xl"
                  />
                </div>
                <div className="flex flex-1 flex-col p-8">
                  <span className="w-fit rounded-full bg-electric-50 px-3 py-1 text-xs font-semibold text-electric-600">
                    {kit.tag}
                  </span>
                  <h3 className="mt-5 text-xl font-bold text-navy-900">{t(`kits.items.${kit.key}.title`)}</h3>
                  <ul className="mt-4 space-y-2 text-base text-gray-600">
                    {t(`kits.items.${kit.key}.list`, { returnObjects: true }).map((item) => (
                      <li key={item} className="flex gap-2">
                        <span className="text-electric-500">&bull;</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                  <p className="mt-5 text-sm text-gray-400">{t(`kits.items.${kit.key}.modules`)}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* CIERRE */}
      <div className="isolate relative flex min-h-screen items-center overflow-hidden bg-gradient-to-br from-navy-950 via-navy-900 to-electric-700 py-24">
        <div
          className="absolute inset-x-0 -top-40 -z-10 transform-gpu overflow-hidden blur-3xl sm:-top-80"
          aria-hidden="true"
        >
          <div
            className="relative left-[calc(50%-11rem)] aspect-[1155/678] w-[50rem] -translate-x-1/2 rotate-[30deg] bg-gradient-to-tr from-electric-400 to-neon-300 opacity-20 sm:left-[calc(50%-30rem)] sm:w-[100rem]"
            style={{
              clipPath:
                'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)',
            }}
          />
        </div>
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="max-w-xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-sm font-medium text-neon-300">
              {t('cierre.badge')}
            </span>
            <p className="mt-6 text-3xl font-bold tracking-tight text-white sm:text-4xl">
              {t('cierre.heading')}
            </p>
            <p className="mt-4 text-lg leading-8 text-white/70">
              {t('cierre.description')}
            </p>
          </div>
          <div className="mt-12 mx-auto grid max-w-5xl grid-cols-1 divide-y divide-white/10 rounded-2xl border border-white/15 bg-white/[0.03] backdrop-blur-sm sm:grid-cols-2 sm:divide-y-0 sm:divide-x lg:grid-cols-4">
            {salidasData.map((salida) => (
              <div key={salida.key} className="flex flex-col p-7">
                <salida.icon
                  className={`h-7 w-7 ${salida.highlighted ? 'text-neon-300' : 'text-white/40'}`}
                  aria-hidden="true"
                />
                <span className="mt-4 text-base font-bold text-white">{t(`cierre.salidas.${salida.key}.label`)}</span>
                <span className="mt-2 text-sm leading-6 text-white/60">{t(`cierre.salidas.${salida.key}.description`)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
